import { Injectable } from 'angular-ts-decorators';
import { each, isNumber, sample } from 'lodash';

import type { Uuid } from '@shared/app/scripts/vendor/uuid';
import { BaseTransactionSkuModel } from '@shared/app/scripts/classes/base-transaction-model';
import { FashionChequeResponse } from './fashion-cheque';
import {
  SaleCouponModel,
  SaleModel,
} from '@shared/app/scripts/classes/sale-model';
import { SalesManager } from '@shared/app/scripts/services/sales-manager';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { Translate } from '@shared/app/scripts/services/translate';
import { FashionCheque } from '@shared/app/scripts/services/fashion-cheque';

import {
  ceil,
  filter,
  find,
  floor,
  inRange,
  isNaN,
  matches,
  round,
  toInteger,
} from 'lodash';

export interface IBarcodeItem {
  type: string;
  price: number;
  qty: number;
  nettPrice: number;
  couponNr?: string;
}

/**
 * This service contains various calculations for transactions. Some change the
 * transaction, some only return values. When loading a historical transaction, use
 * the preCalc function to recalculate values.
 * Transaction Model
 * -----------------
 *
 * @attrs updateTotals {function} Use this function always in between changing values and reading line totals.
 * @attrs total {function}
 * @attrs totalDiscount {function}
 * @attrs outstandingAmountDue {function}
 * @attrs customerName {function}
 * @attrs lineType {function} takes line, returns string based on type.
 * @attrs showLineDiscount {function} Takes line, returns boolean
 * @attrs lineDiscount {function} Takes line, returns discount on line.
 * @attrs lineTotal {function} Takes line, returns it's total.
 */
@Injectable('salesCalculations')
export class SalesCalculations {
  private $translate: Translate;
  private readonly TRANSACTION_RECEIVED: number = 1;
  private readonly TRANSACTION_SOLD: number = 2;
  private readonly TRANSACTION_TRANSIT: number = 3;
  private readonly TRANSACTION_CHANGE: number = 4;
  private readonly TRANSACTION_RETURN_SUPPLIER: number = 5;
  private readonly TRANSACTION_RETURN_CUSTOMER: number = 6;
  private readonly TRANSACTION_INVENTORY: number = 7;
  private readonly TRANSACTION_CONSIGNMENT: number = 9;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_PO: number = 99;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_OFFER: number = 98;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_PREORDER: number = 97;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_ORDER: number = 96;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_PREPICKLIST: number = 95;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_PICKLIST: number = 94;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly TRANSACTION_PRESOLD: number = 93;

  constructor(
    translate: Translate,
    private $log: ng.ILogService,
    private settingsManager: SettingsManager,
    private Money,
    private salesManager: SalesManager,
    private uuid: Uuid,
    private fashionCheque: FashionCheque,
  ) {
    'ngInject';

    this.$translate = translate;
  }
  private castToMoney(value: number) {
    return new this.Money(round(value * 100), this.Money.EUR);
  }

  /**
   * @param {object} transaction a sales transaction
   * @returns {boolean} true if is cash only transaction
   */
  isCashOnly(transaction): boolean {
    let payments = transaction.payments,
      transType = '',
      payment;
    for (payment in payments) {
      if (payment === 'cash' && payments[payment] !== 0) {
        transType += 'cash';
      } else if (payments[payment] !== 0) {
        transType += 'other';
      }
    }
    // POS-341: only round on actual cash, not by default
    return transType === 'cash' ? true : false;
  }

  /**
   * @param {object} line a line object
   * @returns {string} name of the type of the line
   */
  lineType(line: IBarcodeItem): string {
    if (line.type === 'A' || line.type === 'KA') {
      return this.$translate.instant('shared.voucher');
    }
    if (line.type === 'C' || line.type === 'KC') {
      return this.$translate.instant('shared.cashback_coupon');
    }
    if (line.type === ' ' || line.type === 'K') {
      return this.$translate.instant('shared.discount_coupon');
    }
    if (line.type === 'I' || line.type === 'KI') {
      return this.$translate.instant('shared.coupon_sold');
    }
    if (line.type === 'U' || line.type === 'KU') {
      if (this.fashionCheque.validateCode(line?.couponNr)) {
        return 'Fashioncheque';
      }
      return this.$translate.instant('shared.redeemed_coupon');
    }
    if (line.type === 'T' || line.type === 'KT') {
      return this.$translate.instant('shared.credit_receipt');
    }
    if (line.type === 'O') {
      return this.$translate.instant('shared.store_credit');
    }
    return line.type;
  }

  /**
   * @param {object} transaction the transaction
   * @returns {number} Returns a non negative number of change
   */
  changeAbsolute(transaction: SaleModel): number {
    if (transaction) {
      if (transaction.change > 0) {
        return transaction.change;
      } else {
        return -transaction.change;
      }
    }
  }

  /**
   * @param {object} transaction the transaction
   * @returns {string} Translated text for change
   */
  changeText(transaction: SaleModel): string {
    if (transaction) {
      if (transaction.change > 0) {
        return this.$translate.instant('shared.change');
      } else {
        return this.$translate.instant('shared.owe');
      }
    }
  }

  /**
   * @param {object} transaction the transaction
   * @returns {number} Outstanding amount
   */
  outstandingAmountDue(transaction: SaleModel): number {
    if (!transaction) {
      return;
    }
    return round(-transaction.change - transaction.difference, 2);
  }

  /**
   * @param {object} transaction the transaction
   * @returns {number} the total discount
   */
  totalDiscount(transaction: SaleModel): number {
    let discounts = 0;
    let r = transaction.receipt;
    if (r) {
      for (let i = 0; i < r.length; i++) {
        let line = r[i];

        discounts +=
          line.category === 'barcode' && line.qty > 0
            ? (line.nettPrice - line.price) * line.qty
            : 0;
      }
    }

    //discount per line + discount given over whole receipt + coupons
    return (
      round(discounts, 2) +
      transaction.overallReceiptDiscount +
      transaction.totalDiscountCoupon
    );
  }

  /**
   * @param {object} transaction the transaction
   * @returns {number} the total amount
   */
  total(transaction: SaleModel): number {
    if (transaction.type === 'withdrawal') {
      return transaction.payments.cash;
    }

    const overallReceiptDiscount = transaction.overallReceiptDiscount
      ? transaction.overallReceiptDiscount
      : 0;

    return (
      transaction.totalAmount -
      overallReceiptDiscount -
      transaction.totalDiscountCoupon -
      transaction.totalCoupon
    );
  }

  /**
   * @param {object} transaction the transaction
   * @returns {number} the total cashback
   */
  totalCashback(transaction: SaleModel): number {
    let base = this.settingsManager.settings.loyalty?.cashback
      ?.giveCashbackOnDiscounts
      ? transaction.getTotalPaid()
      : transaction.getUndiscountedTotal();

    return round(
      (base * this.settingsManager.settings.loyalty.customerCashback) / 100,
      2,
    );
  }

  /**
   * @param {object} transaction the transaction
   * @returns {string} the customer his name
   */
  customerName(transaction: SaleModel): string {
    if (!transaction || !transaction.customer) {
      return '';
    }

    const nameArray = [];

    if (transaction.customer.title) {
      nameArray.push(transaction.customer.title);
    }

    if (transaction.customer.firstname) {
      nameArray.push(transaction.customer.firstname);
    }

    if (transaction.customer.middlename) {
      nameArray.push(transaction.customer.middlename);
    }

    if (transaction.customer.lastname) {
      nameArray.push(transaction.customer.lastname);
    }

    return nameArray.join(' ');
  }

  /**
   * @param {object} transaction the transaction
   * @param {object} line the barcode object
   * @returns {boolean} if the line should be shown
   */
  showLineDiscount(transaction: SaleModel, line: IBarcodeItem): boolean {
    if (
      line.price * line.qty &&
      (line.nettPrice > line.price ||
        transaction.overallReceiptDiscount ||
        transaction.totalDiscountCoupon)
    ) {
      return true;
    }
    return false;
  }

  /**
   * @param {object} transaction the transaction
   * @param {object} line the barcode object
   * @returns {boolean} returns the discount of the given line
   */
  lineDiscount(transaction: SaleModel, line: IBarcodeItem): any {
    //dp = discount percentage
    let dp =
      transaction.discountableAmount > 0
        ? (transaction.overallReceiptDiscount +
            transaction.totalDiscountCoupon) /
          transaction.discountableAmount
        : 0;

    //articlediscount + receiptdiscount
    let total = line.nettPrice - line.price + dp * line.price;
    if (isNaN(total)) {
      return '';
    }
    //If both the discount and the total round up, things get nasty
    let total2 = total - round(total, 2);
    if (total2 !== 0) {
      //to prevent this from not working because js turns it into 0.00500...01 or ...02 etc.
      total2 = round(total2, 3);
      if (total2 === -0.005 || total2 === 0.005) {
        total -= 0.001;
      }
    }

    if (transaction.line === line) {
      return this.castToMoney(total - transaction.diff).toDecimal();
    }
    return this.castToMoney(total).toDecimal();
  }

  /**
   * can be a percentage or a monetary value.
   * @param {object} line the barcode object
   * @param {number} discountAmount the amount to discount from the price
   * @param {boolean} isPercentage true to apply a percentage discount, false to apply monetary amount
   * @returns {number} returns the line discount price
   */
  getLineDiscountPrice(
    line: IBarcodeItem,
    discountAmount: number,
    isPercentage: boolean,
  ): number {
    let value;
    if (isPercentage) {
      if (inRange(discountAmount, 0, 100)) {
        discountAmount = round((line.nettPrice * discountAmount) / 100, 2);
        return this.castToMoney(line.nettPrice)
          .subtract(this.castToMoney(discountAmount))
          .toDecimal();
      } else {
        return line.nettPrice;
      }
    } else {
      value = this.castToMoney(line.nettPrice)
        .subtract(this.castToMoney(discountAmount))
        .toDecimal();
      return value >= 0 ? value : line.nettPrice;
    }
  }

  /**
   * @param {object} line the barcode object
   * @param {number} discountAmount the amount to discount from the price
   * @param {boolean} isPercentage true to apply a percentage discount, false to apply monetary amount
   * @returns {number} returns the line discount
   */
  getLineDiscountAmount(
    line: IBarcodeItem,
    discountAmount: number,
    isPercentage: boolean,
  ): number {
    if (isPercentage) {
      if (inRange(discountAmount, 0, 100)) {
        return round((discountAmount / 100) * line.nettPrice, 2);
      } else {
        return 0;
      }
    } else {
      return discountAmount >= line.nettPrice ? 0 : discountAmount;
    }
  }

  /**
   * @param {object} transaction the transaction          *
   * @param {object} line the barcode object
   * @returns {number} returns the total price of the line
   */
  lineTotal(transaction: SaleModel, line: IBarcodeItem): number {
    let lineTotal = 0;

    if (isNaN(line.price) || line.price === undefined) {
      // Keep it 0
    } else if (line.qty < 0) {
      //don't count discount for RMA
      lineTotal = line.qty * line.price;
    } else {
      let dp =
        transaction.discountableAmount > 0
          ? (transaction.totalDiscountCoupon +
              transaction.overallReceiptDiscount) /
            transaction.discountableAmount
          : 0;

      if (transaction.line === line) {
        lineTotal =
          line.qty * (line.price - dp * line.price) + transaction.diff;
      }
      lineTotal = line.qty * (line.price - dp * line.price);
    }

    return round(lineTotal, 2);
  }

  /**
   * @param {object} transaction the transaction          *
   * @param {string} type the type of VAT
   * @returns {number} returns the bruto amount of the transaction
   */
  brutoAmount(transaction: SaleModel, type: string): number {
    //type is 'high', 'low' or 'zero'
    return transaction.vat[type + 'total'] - transaction.vat[type + 'amount'];
  }

  /**
   * Get the detraction of the coupon on the price
   * Coupons explained: http://help.softwear.nl/x/ZwNY
   * A (Saving Points Coupon)
   * T (Credit Receipt/Tegoedbon) for items returned
   * U (Coupon/Kadobon) normal coupon
   * I (Sellable Coupon/Kadobon Verkoop) A coupon a customer can purchase
   * C (Cashback) A type of discount
   * K (Discount Coupon/Waardecheque) A type of discount
   * O Not a coupon but oprekening
   * @param {object} line the barcode object
   * @returns {number} the price of the line based on the type
   */
  linePrice(line: IBarcodeItem): number {
    switch (line.type) {
      case 'A':
      case 'T':
      case 'U':
        return line.price;
      case 'I':
        return -line.price;
      case 'C':
      case ' ':
        return line.price;
      case 'O':
        return -line.price;
    }

    //since not lmited to coupons anymore, a default:
    return line.price;
  }

  /**
   * This includes the following transaction properties:
   * - **totalAmount** is the total amount due by the consumer. This can be a
   * negavtive value when money is due to the consumer.
   * - **totalDiscont** is the total amount of discounts on the sales transaction.
   * These are discounts given manually at the POS by the cashier using the total
   * discount or total discount percentage salesCalculations.
   * - **totalCoupon**  is the total amount of coupons on the sales transaction.
   * - **totalDiscountCoupon** is the total amount of discount coupons on the transaction.
   * Discount coupons are treated differently than standard coupons in regards to how
   * discounts are shown on the receipt. Discount coupons act as a discount over individual items.
   * Standard coupons subtract from the total amount that a consumer should pay.
   * - **totalNumber** is not a monetary amount, but the total quantity of items purchased.
   * RMA (returns) and coupons doesn't count for total number of items.
   * - **totalStoreCreditPaid** is the total amount of store credit payments on the transaction.
   * @param {object} transaction the sales transaction object
   * @returns {Void} returns nothing
   */
  updateTotals(transaction: SaleModel): void {
    transaction.totalNumber = 0;
    transaction.totalReturn = 0;
    transaction.totalAmount = 0;
    transaction.totalCoupon = 0;
    transaction.totalDiscountCoupon = 0;
    transaction.totalStoreCreditPaid = 0;

    this.calcDiscountableAmount(transaction);

    if (transaction.discountableAmount < transaction.overallReceiptDiscount) {
      transaction.overallReceiptDiscount = transaction.discountableAmount;
    }

    each(transaction.receipt, (item) => {
      if (!item) {
        return;
      }

      item.lineTotal = 0;

      if (item.couponNr && !(item.type === 'I' || item.type === 'KI')) {
        //Calculate total discount coupons. These are coupons of type K (Discount Coupon) or KC (Cashback)
        if (item.type === ' ' || item.type === 'C') {
          item.lineTotal = 0;
          transaction.totalDiscountCoupon += item.value;
        } else {
          // aparently any type of coupon; especially T/TK (store credit)
          item.lineTotal = this.linePrice(item);
          transaction.totalCoupon += item.lineTotal;
        }
      } else {
        if (item.qty > 0) {
          transaction.totalNumber += item.qty;
        }
        if (item.qty < 0) {
          transaction.totalReturn -= item.qty;
        }

        transaction.totalAmount += item.qty * item.price;

        if (isNumber(item.qty) && isNumber(item.price)) {
          item.lineTotal = this.lineTotal(transaction, item);
        }
      }
      if (item.category === 'storecredit') {
        transaction.totalStoreCreditPaid += item.price;
      }
    });

    transaction.totalAmount = round(transaction.totalAmount, 2);

    // totalCoupon excludes discount coupons
    transaction.totalCoupon = round(transaction.totalCoupon, 2);

    // total discount coupons
    transaction.totalDiscountCoupon = round(transaction.totalDiscountCoupon, 2);
  }

  /**
   * Properties updated by this function:
   * - **totalPaid** is the total amount paid by the consumer. This is a sum of all the values in the `transaction.payments` object.
   * @param {object} transaction the sales transaction object
   * @returns {Void} returns nothing
   */
  updateTotalPaid(transaction: SaleModel): void {
    //Calculate all payment methods.
    let totalPaid = 0;
    for (let i in transaction.payments) {
      totalPaid += transaction.payments[i];
    }
    //Update total Amount Paid
    transaction.totalPaid = round(totalPaid, 2);
    // TODO Remove? we have two change calculations... which one is correct?? This one seems to lose... See updateGrandTotal()
    transaction.change = round(
      transaction.totalAmount -
        transaction.totalDiscountCoupon -
        transaction.totalCoupon -
        transaction.overallReceiptDiscount -
        transaction.totalPaid,
      2,
    );

    //calc 1cent diff per line and total it up
    this.calcDiff(transaction);
  }

  /**
   * any rounding to the nearest 5 cent depending on the roundTo5Cent boolean.
   * This function modifies the following properties:
   * - **change** see calcChange for an explantaion of how change is calculated.
   * - **difference** see calcDiff for an explaination of the difference property.
   * @param {Object} transaction the transaction Object
   * @param {boolean} roundTo5Cent a setting which will round change up or down
   * to the nearest 5 cent value
   * @returns {Void} returns nothing
   */
  updateGrandTotal(transaction: SaleModel, roundTo5Cent: boolean): void {
    let isCashOnly = this.isCashOnly(transaction);
    let change = this.calcChange(transaction);

    // round totalAmount to 5 cent only for CASH payments (round5)
    if (isCashOnly && roundTo5Cent && change !== 0) {
      transaction.change = this.roundTo5Cent(change);
      transaction.difference = round(change - transaction.change, 2);
      this.$log.info(`Change Rounded: ${transaction.change}`);
    } else {
      transaction.change = change;
      transaction.difference = 0;
    }
  }

  /**
   * - **change** is the change due to the consumer. Change occurs when the consumer pays more than
   * the amount due. Example: The amount due is 25.00. The consumer pays 30.00. The change is 5.00.
   * Change is a calculation of the `transaction.totalPaid - transaction.totalAmount + transaction.totalDiscount + transaction.totalDiscountCoupon + transaction.totalCoupon`
   * @param {Object} transaction the transaction Object
   * @return {number} change the amount of change
   */
  calcChange(transaction: SaleModel): number {
    return round(
      transaction.totalPaid -
        transaction.totalAmount +
        transaction.overallReceiptDiscount +
        transaction.totalDiscountCoupon +
        transaction.totalCoupon,
      2,
    );
  }

  /**
   * @param {object} transaction the transaction object
   * @returns {Void} returns nothing
   */
  calcDiscountableAmount(transaction: SaleModel): void {
    transaction.discountableAmount = 0;
    let num = transaction.receipt.length;
    let item;
    let price;
    let discountableAmount;

    for (let r = 0; r < num; r++) {
      item = transaction.receipt[r];
      //not coupons or oprekening or anything else we might think of.
      if (item && item.category === 'barcode' && item.qty > 0) {
        price = this.castToMoney(item.price);
        discountableAmount = this.castToMoney(transaction.discountableAmount);
        transaction.discountableAmount = discountableAmount
          .add(price.multiply(item.qty))
          .toDecimal();
      }
    }
  }

  /**
   * For example if rounding is turned on, and the balance due is 11.99. The amount due is rounded
   * to 12.00. The remaining 1 cent is added to the difference property on the transaction.
   * @param {object} transaction the transaction object
   * @returns {Void} returns nothing
   */
  calcDiff(transaction: SaleModel): void {
    // NOTE those fields are removed by the backend. As we were using
    // global fields before, the function is probably called every time
    // *before* those values are required.
    transaction.diff = 0;
    transaction.line = false;
    if (transaction.overallReceiptDiscount || transaction.totalDiscountCoupon) {
      let p;
      let line;
      for (let j in transaction.receipt) {
        line = transaction.receipt[j];
        if (!line.couponNr) {
          p = this.lineTotal(transaction, line);
          transaction.diff += p - round(p, 2);
          if (!transaction.line) {
            transaction.line = line;
          }
        }
      }
    }
  }

  /**
   * @param {object} transaction the transaction object
   * @returns {Void} returns nothing
   */
  preCalc(transaction: SaleModel): void {
    this.calcDiff(transaction);
    this.calcDiscountableAmount(transaction);
  }

  /**
   * @param {object} transaction the transaction object
   * @param {object} config the pos configuration object
   * @returns {string} returns receipt number
   */
  // TODO remove; or cover with tests
  // TODO refactor it out
  receiptCode(transaction: SaleModel, config): string {
    let s = '';
    if (config) {
      s =
        config.cashregisterNr +
        '-' +
        config.deviceNr +
        '-' +
        this.salesManager.counters.sales;
      for (
        let i = this.salesManager.counters.sales.toString().length;
        i < 5;
        i++
      ) {
        s = '0' + s;
      }
    }
    if (transaction.nr) {
      s = transaction.nr;
    }
    return s;
  }

  /**
   * @param {object} transaction the transaction object
   * @param {object} vatValues an object containing the values used to calculate VAT
   * @returns {Void} returns nothing
   */
  calculateVAT(transaction: SaleModel, vatValues): void {
    if (!transaction || !vatValues) {
      this.$log.error('[salesCalculations] Missing transaction or vatValues!');
      return;
    }
    //VAT total amount
    let highamount = 0;
    let hightotal = 0;
    let lowamount = 0;
    let lowtotal = 0;
    let zeroamount = 0;
    let zerototal = 0;
    //VAT Values
    let high = vatValues.highvalue;
    let low = vatValues.lowvalue;
    let zero = vatValues.zerovalue;
    let receipt = transaction.receipt;
    let discountPerLine = 0;
    //SWUI-1171 VAT should not include total receipt discount or coupons which are used as discounts (K and KC)
    if (transaction.totalAmount && transaction.totalAmount !== 0) {
      discountPerLine =
        (transaction.overallReceiptDiscount + transaction.totalDiscountCoupon) /
        transaction.totalAmount;
    }

    // Only articles (category 'barcode') are added up here, with one exception:
    // KA coupons need to be treated as an article with negative price in Order
    // for the VAT corrections to be correct (see POS-1062).
    let i;
    for (i = 0; i < receipt.length; i++) {
      if (
        receipt[i].category !== 'barcode' &&
        !(receipt[i].category === 'coupon' && receipt[i].type === 'A')
      ) {
        continue;
      }

      // The first if-statement handles KA coupons (see POS-1062). These are assumed to have high
      // VAT. In the rare case that only low-VAT articles are bought, the numbers
      // may turn out wrong (i.e. different from Foxpro). Rare errors are acceptable since
      // Foxpro does the VAT calculations that are leading for the financial administration.
      if (receipt[i].category === 'coupon' && receipt[i].type === 'A') {
        highamount -=
          (1 - discountPerLine) *
          receipt[i].price *
          (high / (high + 100)) *
          receipt[i].qty;
        hightotal -= (1 - discountPerLine) * receipt[i].price * receipt[i].qty;
      } else if (high === receipt[i].vat) {
        highamount +=
          (1 - discountPerLine) *
          receipt[i].price *
          (high / (high + 100)) *
          receipt[i].qty;
        hightotal += (1 - discountPerLine) * receipt[i].price * receipt[i].qty;
      } else if (low === receipt[i].vat) {
        lowamount +=
          (1 - discountPerLine) *
          receipt[i].price *
          (low / (low + 100)) *
          receipt[i].qty;
        lowtotal += (1 - discountPerLine) * receipt[i].price * receipt[i].qty;
      } else if (zero === receipt[i].vat) {
        zeroamount +=
          (1 - discountPerLine) *
          receipt[i].price *
          (zero / (zero + 100)) *
          receipt[i].qty;
        zerototal += (1 - discountPerLine) * receipt[i].price * receipt[i].qty;
      }
    }

    if (!angular.isObject(transaction.vat)) {
      // @ts-ignore
      transaction.vat = {};
    }

    transaction.vat.highamount = round(highamount, 2);
    transaction.vat.lowamount = round(lowamount, 2);
    transaction.vat.zeroamount = round(zeroamount, 2);

    transaction.vat.hightotal = round(hightotal, 2);
    transaction.vat.lowtotal = round(lowtotal, 2);
    transaction.vat.zerototal = round(zerototal, 2);

    transaction.vat.highvalue = high;
    transaction.vat.lowvalue = low;
    transaction.vat.zerovalue = zero;
  }

  /**
   * @param {object} transaction the transaction object
   * @param {object} loyaltyRules an object containing the values used to calculate loyalty points
   * @returns {Void} returns nothing
   */
  calculateLoyaltyPoints(transaction: SaleModel, loyaltyRules): void {
    if (!transaction || !loyaltyRules) {
      this.$log.error(
        '[salesCalculations] Missing transaction or loyaltyRules!',
      );
      return;
    }

    if (
      loyaltyRules.calculatePointsPerReceipt !== true ||
      loyaltyRules.pointValue === 0
    ) {
      return;
    }

    let ka, i;
    let loyaltyPoints = 0;
    let receipt = transaction.receipt;

    //If consignment, do not calculate loyalty points
    if (
      transaction.payments &&
      transaction.payments.consignment &&
      transaction.payments.consignment > 0
    ) {
      //Don't calculate loyalty points
    } else {
      let discount = false;
      for (let j = 0; j < receipt.length; j++) {
        if (receipt[j].couponNr && receipt[j].type === ' ') {
          discount = true;
        }
      }

      for (i = 0; i < receipt.length; i++) {
        if (receipt[i].category !== 'barcode') {
          continue; //Only barcodes
        } else if (receipt[i].couponNr && receipt[i].type === 'A') {
          //
          continue;
        }

        if (loyaltyRules && transaction.customer) {
          if (
            (receipt[i].couponNr && receipt[i].type === 'I') ||
            !receipt[i].couponNr
          ) {
            if (!loyaltyRules.suppressPointsOnDiscount) {
              loyaltyPoints +=
                (receipt[i].price * receipt[i].qty) / loyaltyRules.pointValue;
            } else if (
              receipt[i].nettPrice <= receipt[i].price &&
              transaction.overallReceiptDiscount === 0 &&
              transaction.totalDiscountCoupon === 0 &&
              !discount
            ) {
              loyaltyPoints +=
                (receipt[i].price * receipt[i].qty) / loyaltyRules.pointValue;
            }
          }
        }
      }

      /*
       * POS-62
       * Subtract the point balance from the balance if there are any KA coupons
       */
      ka = filter(receipt, matches({ type: 'A', category: 'coupon' }));
      if (ka.length) {
        for (i in ka) {
          loyaltyPoints -= ka[i].price / loyaltyRules.pointValue;
        }
        // Should not take the point balance into the negative if the coupon value
        // is higher than the receipt total point balance
        loyaltyPoints = loyaltyPoints >= 0 ? loyaltyPoints : 0;
      }

      const kCoupons = filter(receipt, (element) => {
        return (
          element.category === 'coupon' &&
          (element.type === 'K' || element.type === ' ')
        );
      });
      if (kCoupons.length) {
        for (i in kCoupons) {
          loyaltyPoints -= kCoupons[i].value / loyaltyRules.pointValue;
        }
        // Should not take the point balance into the negative if the coupon value
        // is higher than the receipt total point balance
        loyaltyPoints = loyaltyPoints >= 0 ? loyaltyPoints : 0;
      }

      /*
       * POS-1514 Subtract the point balance from the balance if there are any receipt discounts
       */
      if (
        transaction.overallReceiptDiscount > 0 &&
        !loyaltyRules.suppressPointsOnDiscount
      ) {
        loyaltyPoints -=
          transaction.overallReceiptDiscount / loyaltyRules.pointValue;

        // Should not take the point balance into the negative if the coupon value
        // is higher than the receipt total point balance
        loyaltyPoints = loyaltyPoints >= 0 ? loyaltyPoints : 0;
      }
    }

    if (transaction.customer && loyaltyRules) {
      // rounding down & multiply points by the point factor
      loyaltyPoints = this.roundDown(loyaltyPoints) * loyaltyRules.pointFactor;

      loyaltyPoints += isNaN(transaction.customer.points)
        ? 0
        : toInteger(transaction.customer.points);

      // rounding down, just in case if somebody adds 'pointFactor' < 1
      transaction.loyaltyPoints = this.roundDown(loyaltyPoints);
    }
  }

  /**
   * @param {Number} original the number to round
   * @returns {Number} returns rounded number
   */
  roundDown(original: number): number {
    if (original > 0) {
      // Positive numbers
      return floor(original);
    } else {
      // Negative numbers
      return ceil(original);
    }
  }

  /**
   * This method is used by the templating engine.
   * @param {object} transaction the transaction object
   * @returns {Void} returns nothing
   */
  getExpandedReceiptInfo(transaction: SaleModel): any[] {
    let receipt = angular.copy(transaction.receipt);

    for (let i = 0; i < receipt?.length; i++) {
      let line = receipt[i];
      line.showDiscount = this.showLineDiscount(transaction, line);
      line.discount = this.lineDiscount(transaction, line);
      line.total = this.lineTotal(transaction, line);
      line.lineType = this.lineType(line);
      if (line.lineType) {
        line.linePrice = -this.linePrice(line);
      }
    }

    return receipt;
  }

  /**
   * This method is used by the templating engine.
   * @param {object} transaction the transaction object
   * @returns {Void} returns nothing
   */
  hasDiscountedArticles(transaction: SaleModel): boolean {
    let hasDiscount = false;

    for (let i = 0; i < transaction.receipt?.length; i++) {
      let line = transaction.receipt[i];

      if (this.showLineDiscount(transaction, line)) {
        hasDiscount = true;
      }
    }

    return hasDiscount;
  }

  /**
   * @param {object} object an object
   * @returns {Array} returns an array
   */
  // TODO to be removed, nothing use it now, and we have _
  objectToArray(object): object[] {
    let a = [];
    for (let i in object) {
      a.push({
        key: i,
        value: object[i],
      });
    }
    return a;
  }

  /**
   * Changes the `transaction.receipt[n].price` directly and resets the `transaction.totalDiscount` to 0.
   * @param {object} transaction the transaction object
   * @returns {Void} returns nothing
   */
  spreadTotalDiscount(transaction: SaleModel): void {
    let nettPrice, lineDiscount;

    for (let l in transaction.receipt) {
      let line = transaction.receipt[l];
      if (line && line.category === 'barcode' && line.qty > 0) {
        nettPrice = this.castToMoney(line.nettPrice);
        lineDiscount = this.castToMoney(this.lineDiscount(transaction, line));
        //line.price = round(line.nettPrice - salesCalculations.lineDiscount(transaction, line), 2);
        line.price = nettPrice.subtract(lineDiscount).toDecimal();
      }
    }

    transaction.totalDiscount = 0;
  }

  /**
   * Method that checks if there is a store credit payment in the transaction.
   * @param {object} transaction
   * transaction object to be checked
   * @return {boolean} result
   */
  containsStoreCredit(transaction: SaleModel): boolean {
    if (
      !angular.isObject(transaction) ||
      !angular.isArray(transaction.receipt)
    ) {
      return false;
    }

    return !!find(
      transaction.receipt,
      matches({
        category: 'storecredit',
      }),
    );
  }

  /**
   * This method creates the Credit Receipt or the Cashback coupon. These
   * coupons are given to the customer post sale and can be redeemed only
   * on a future sale.
   * - For more information about Cashback coupons see {@link https://help.softwear.nl/display/SWUI/Coupons#Coupons-Cashback-KC the documentation}.
   * - For more information about Credit Receipts see {@link https://help.softwear.nl/display/SWUI/Coupons#Coupons-CreditCoupon-KT the documentation}.
   * @param {object} transaction
   * transaction object to be modified
   * @returns {Void} returns nothing
   */
  generatePostSaleCoupon(transaction: SaleModel): void {
    let couponsArray: SaleCouponModel[] = [];

    let isCreditReceipt = !!transaction.payments.creditreceipt,
      isCashback =
        transaction.customer &&
        this.settingsManager.settings.loyalty.customerCashback > 0 &&
        this.totalCashback(transaction) > 0 &&
        transaction.payments.consignment === 0;

    if (isCreditReceipt) {
      couponsArray.push({
        id: this.generateRandomAlphaNumeric(13),
        value: -transaction.payments.creditreceipt,
        type: 'T',
      });
    }

    if (isCashback) {
      couponsArray.push({
        id: this.generateRandomAlphaNumeric(13),
        value: this.totalCashback(transaction),
        type: 'C',
      });
    }

    if (couponsArray.length !== 0) {
      transaction.coupon = couponsArray;
    }
  }

  /**
   * Returns the amount rounded to the nearest 5 cents
   * @param {Number} amount the amount to be rounded
   * @returns {Number} the amount rounded to the nearest 5 cents
   */
  roundTo5Cent(amount: number): number {
    // Round to the second decimal fraction
    amount = Math.round(amount * Math.pow(10, 2)) / Math.pow(10, 2);

    let cents = amount % 0.1;
    // Round to the second decimal fraction
    cents = Math.abs(cents);
    cents = Math.round(cents * Math.pow(10, 2)) / Math.pow(10, 2);

    if (0 < cents && cents <= 0.02) {
      amount = amount < 0 ? amount + cents : amount - cents;
    } else if (0.03 <= cents && cents <= 0.07) {
      amount = amount < 0 ? amount + cents - 0.05 : amount - cents + 0.05;
    } else if (0.08 <= cents && cents < 0.1) {
      amount = amount < 0 ? amount + cents - 0.1 : amount - cents + 0.1;
    }
    // Round to the second decomil fraction
    amount = Math.round(amount * Math.pow(10, 2)) / Math.pow(10, 2);

    return amount;
  }

  /**
   * Returns a random alphanumeric number of a given length
   * @param {Number} length the amount to be rounded
   * @param {Number} chars the type of characters to return
   * @returns {String} the randomly generated string
   */
  // TODO simplify, use _ if possible
  // TODO remove; or cover with tests
  generateRandomAlphaNumeric(size: number): string {
    let chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZ';

    let randomString = '';

    for (let i = 0; i < size; i++) {
      randomString += sample(chars);
    }

    return randomString;
  }

  /**
   * Return a transaction with type set
   * @param {Object} transaction the transaction object
   * @param {String} type the string transaction type
   * @return {Object} changed transaction
   */
  // TODO remove; or cover with tests
  setType(transaction: SaleModel, type: string): SaleModel {
    //Insert types
    switch (type) {
      case 'received':
        transaction.type = this.TRANSACTION_RECEIVED;
        break;
      case 'sale':
        transaction.type = this.TRANSACTION_SOLD;
        break;
      case 'transit':
        transaction.type = this.TRANSACTION_TRANSIT;
        break;
      case 'change':
        transaction.type = this.TRANSACTION_CHANGE;
        break;
      case 'return_supplier':
        transaction.type = this.TRANSACTION_RETURN_SUPPLIER;
        break;
      case 'return_customer':
        transaction.type = this.TRANSACTION_RETURN_CUSTOMER;
        break;
      case 'inventory':
        transaction.type = this.TRANSACTION_INVENTORY;
        break;
      case 'consignment':
        transaction.type = this.TRANSACTION_CONSIGNMENT;
        transaction.status = 'open';
        break;
    }

    return transaction;
  }

  /**
   * Check if transaction has any payment method with a non-zero value
   * @param transaction the transaction object
   * @return true if there is any payment different then 0
   */
  hasAnyPayment(transaction: SaleModel): boolean {
    if (
      angular.isObject(transaction) &&
      angular.isObject(transaction.payments)
    ) {
      return !!find(transaction.payments, (value) => {
        return !!value;
      });
    }

    return false;
  }

  /**
   * Return a transaction with superfluous fields removed
   * @param transaction the transaction object
   * @return true if there is any payment different then 0
   */
  isEmpty(transaction: SaleModel): boolean {
    return this.hasAnyPayment(transaction) || transaction?.receipt?.length > 0;
  }

  /**
   * Split a single line param1[param2] into multiple lines of 1 qty each.
   * Returns the index of the last item.
   * @param receipt
   * @param index position of the line you want to be split
   * @return index position of the last line that's part of the split
   */
  // TODO add unit tests
  splitReceiptLine(receipt: BaseTransactionSkuModel[], index: number): number {
    //negatives to accomedate retours
    const tqty = Math.abs(receipt[index].qty);
    const qty = receipt[index].qty > 0 ? 1 : -1;

    for (let k = 1; k < tqty; k++) {
      const copy = angular.copy(receipt[index]);
      copy.qty = qty;
      delete copy.reqid;
      receipt.splice(index, 0, copy);
      index++;
    }
    receipt[index].qty = qty;

    return index;
  }

  /*
    Sets fashion cheque coupon to the lesser of the outstanding amount (open to pay)
    or the fashion cheque balance. Uses outstanding amount so that when scanning
    multiple fashion cheques, each is capped at the remaining amount after previous
    fashion cheques, not the original receipt total.
  */
  setFashionChequeToTotal(
    fashionChequeBalance: number,
    transaction: SaleModel,
  ): number {
    const outstandingAmount = this.total(transaction);
    return fashionChequeBalance >= outstandingAmount && outstandingAmount > 0
      ? outstandingAmount
      : fashionChequeBalance;
  }

  // adds a fashionCheque coupon to the receipt
  addFashionChequeToRecepit(
    validationResponse: FashionChequeResponse,
    transaction: SaleModel,
    reqid: any,
  ): any {
    if (validationResponse.status === 'ok') {
      const targetReceipt = transaction.receipt.find(
        (receipt) => receipt.reqid === reqid,
      );

      if (targetReceipt) {
        const fashionCheuqueEuroAmount = Number(
          (validationResponse.data.card.amount.value / 100).toFixed(2),
        );

        targetReceipt.found = true;
        targetReceipt.category = 'coupon';
        targetReceipt.type = 'U';
        targetReceipt.couponNr = validationResponse.data.card.number;
        targetReceipt.qty = 1;
        targetReceipt.price = this.setFashionChequeToTotal(
          fashionCheuqueEuroAmount,
          transaction,
        );
        targetReceipt.nettPrice = this.setFashionChequeToTotal(
          fashionCheuqueEuroAmount,
          transaction,
        );
        targetReceipt.couponBalance = fashionCheuqueEuroAmount;

        return targetReceipt;
      } else {
        return null;
      }
    }

    return null;
  }

  /**
   * Add line to receipt array
   * @param transaction that is updated
   * @param receiptLine dataplaceholder
   * @return updated reqid
   */
  addTemporaryReceiptLine(
    transaction: SaleModel,
    receiptLine: any = {},
  ): string {
    const reqid = this.uuid.v4();
    receiptLine.reqid = reqid;
    transaction.receipt.push(receiptLine);
    return reqid;
  }
}
